<template>
  <div
    id="app"
    :class="{ 'clear-layout': isClearHeader, 'unauthorized-user': !isLogin }"
  >
    <template v-if="isLandingPageOnly">
      <router-view />
      <Toast
        :breakpoints="{ '767px': { width: '96%', right: '2%', left: '2%' } }"
      />
    </template>
    <div
      v-else-if="isManifestReady"
      class="app-root"
      :class="{ 'hide-header': isClearHeader}"
    >
      <gl-header
        v-if="!isClearHeader && !isNewHeader"
        :with-banner="$route.name === 'landing-page'"
        @avatar-click="handleAvatarClick"
      />
      <div
        id="main-container"
        class="main"
      >
        <transition
          appear
          name="scale-page"
        >
          <router-view />
        </transition>
        <Toast
          :breakpoints="{ '767px': { width: '96%', right: '2%', left: '2%' } }"
        />
        <ReportAbuseBanner
          v-if="$router.currentRoute.meta.showReportAbuseBanner"
          :top="$route.name === 'landing-page'"
        />
        <ConfirmDialog />
        <RedirectToLastRouteModal v-if="getShowRedirectToLastRouteModal" />
      </div>
      <GlFooterMenu
        v-if="!isClearHeader && (isMobile || isTablet)"
        ref="footerMenu"
        :with-banner="
          !($route.name === 'report-abuse' || $route.name === 'landing-page')
        "
      />
    </div>
    <div
      v-else
      class="app-root__loader"
    >
      <gl-loader />
    </div>
    <tooltip />
    <div
      ref="fakeTooltip"
      v-popover:tooltip=""
    />
    <gl-pwa-prompt-modal
      v-if="isPromptOpen"
      v-model="isPromptOpen"
      @confirm="SET_IS_PROMPT_OPEN(false)"
    />
    <AddToHomeScreen />
    <GlobalModalWrapper />
  </div>
</template>

<script>
// Vuex
import { mapMutations, mapState, mapGetters, mapActions } from 'vuex'
// Components
import GlHeader from '@/components/layout/gl-header'
import GlFooterMenu from '@/components/layout/gl-footer-menu'
import RedirectToLastRouteModal from '@/pages/payment/modals/RedirectToLastRouteModal'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import ReportAbuseBanner from '@/pages/report-abuse/components/ReportAbuseBanner'
import GlLoader from '@/components/gl-loader'
import GlobalModalWrapper from '@/components/modals/GlobalModalWrapper'
//mixins
import deviceWidthMixin from '@/assets/mixins/deviceWidthMixin'

import defineAbilityFor from '@/services/defineAbilityFor'
import GlPwaPromptModal from "@/components/gl-pwa-prompt-modal.vue";
import AddToHomeScreen from "@/components/AddToHomeScreen.vue";
// utils
import { dispatchEventToRef } from '@/utils/helpers'
import { createPlansList } from '@/utils/plans'

export default {
  components: {
    AddToHomeScreen,
    GlPwaPromptModal,
    GlHeader,
    GlFooterMenu,
    RedirectToLastRouteModal,
    Toast,
    ConfirmDialog,
    ReportAbuseBanner,
    GlLoader,
    GlobalModalWrapper,
  },
  mixins: [deviceWidthMixin],
  data() {
    return {
      clearHeaderRouteNames: ['register', 'login', 'reset-password', 'confirm', 'enter-code', 'user-agreement', 'access', 'coinify-callback'],
      newHeader: ['landing-page'],
      isManifestReady: false,
      plansLoading: false,
      resizeObserver: null,
    }
  },
  computed: {
    ...mapState('analytics', ['coinType', 'coinData', 'currencyList']),
    ...mapState('sidebar', ['isPromptOpen']),
    ...mapGetters('user', ['isLogin']),
    ...mapGetters('payment', ['getShowRedirectToLastRouteModal']),
    isClearHeader() {
      return !!this.clearHeaderRouteNames.find(r => r === this.$route.name)
    },
    isNewHeader() {
      return !!this.newHeader.find(r => r === this.$route.name)
    },
    isLandingPageOnly() {
      return this.$route.name === 'landing-page'
    },
    isAppReadyForSnapshot() {
      return this.isManifestReady && !this.plansLoading;
    }
  },
  watch: {
    isAppReadyForSnapshot: {
      handler(val) {
        if (val && window.__PRERENDER_INJECTED) {
          this.dispatchPrerenderEvent();
        }
      },
      immediate: true
    },
    $route() {
      this.SET_SHOW_LANDING_MENU(false)
      this.RESET_REPORT_STATE()
      this.hideTooltip()
      if (!this.resizeObserver && !window.__PRERENDER_INJECTED) {
        this.$nextTick(() => {
          this.observeResizeMainContainer()
        })
      }
    },
    coinType: {
      handler(role) {
        this.updateAbilities(role)
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadPlans()
    if (!window.__PRERENDER_INJECTED) {
      this.$nextTick(() => {
        this.observeResizeMainContainer()
        this.documentSize()
      })
    }
  },
  async created() {
    try {
      const manifest = await this.getManifest()
      this.SET_COIN_CURRENCY_LIST(manifest)
      const coin = this.currencyList[0]
      if (coin) {
        this.SET_COIN_DATA(coin)
        this.SET_COIN_TYPE(coin.key)
      }
      await this.updateAbilities(this.coinType)
    } catch (e) {
      console.error('[Prerender Critical Error] Initialization failed:', e)
    } finally {
      this.isManifestReady = true
    }

    this.$root.$on('mouseleave-for-fake-tooltip', () => {
      this.hideTooltip()
    })
    this.$root.$on('handle-document-size', () => {
      this.documentSize()
    })
  },
  methods: {
    ...mapActions('analytics', ['getManifest']),
    ...mapActions('payment', ['fetchPlans']),
    ...mapMutations({
      SET_IS_PROMPT_OPEN: 'sidebar/SET_IS_PROMPT_OPEN',
      SET_SHOW_LANDING_MENU: 'sidebar/SET_SHOW_LANDING_MENU',
      SET_COIN_TYPE: 'analytics/SET_COIN_TYPE',
      SET_COIN_DATA: 'analytics/SET_COIN_DATA',
      SET_COIN_CURRENCY_LIST: 'analytics/SET_COIN_CURRENCY_LIST',
      RESET_REPORT_STATE: 'directions/RESET_REPORT_STATE',
      SET_PLANS: 'payment/SET_PLANS',
      SET_PLANS_LOADING: 'payment/SET_PLANS_LOADING',
    }),
    dispatchEventToRef,
    createPlansList,
    dispatchPrerenderEvent() {
      setTimeout(() => {
        const doc = document.documentElement
        doc.style.removeProperty('--full-width')
        doc.style.removeProperty('--doc-height')
        doc.style.removeProperty('--header-height')
        doc.style.removeProperty('--report-header-banner-height')
        console.log('[App.vue] Snapshot Ready Signal Dispatched');
        document.dispatchEvent(new Event('x-render-ready'));
      }, 500);
    },
    handleAvatarClick() {
      this.$refs.footerMenu._data.showProfileSelect = !this.$refs.footerMenu._data.showProfileSelect
    },
    updateAbilities() {
      this.$ability.update(defineAbilityFor(this.coinType).rules)
    },
    async setHeaderHeight() {
      const doc = document.documentElement
      const header = document.querySelector('.header-wrapper')
      if (header) {
        doc.style.setProperty('--header-height', `${header.clientHeight}px`)
      }
      const reportHeaderBanner = document.querySelector('.report-top__banner-wrapper')
      if (reportHeaderBanner) {
        doc.style.setProperty('--report-header-banner-height', 'auto');
        doc.style.setProperty('--report-header-banner-height', `${reportHeaderBanner.clientHeight}px`)
      }
    },
    async setWidth() {
      const doc = document.documentElement
      const main = document.querySelector('.main')
      if (main) {
        doc.style.setProperty('--full-width', `${main.clientWidth}px`)
      }
      setTimeout(async () => {
        this.setHeaderHeight()
      }, 1500)
    },
    documentSize() {
      const doc = document.documentElement
      doc.style.setProperty('--doc-height', `${window.innerHeight}px`)

      this.$nextTick(async () => {
        await this.setHeaderHeight()
        this.setWidth()
      })
    },
    observeResizeMainContainer() {
      if (this.resizeObserver) return
      const mainContainer = document.getElementById('main-container')
      if (!mainContainer) return
      this.resizeObserver = new ResizeObserver(this.documentSize)
      this.resizeObserver.observe(mainContainer)
    },
    loadPlans() {
      this.plansLoading = true;
      this.SET_PLANS_LOADING(true)

      this.fetchPlans().then(({ data, success }) => {
            if (success) {
              this.SET_PLANS(this.createPlansList({ plans: data }))
            }
          })
          .finally(() => {
            this.SET_PLANS_LOADING(false)
            this.plansLoading = false;
          })
    },
    hideTooltip() {
      const refEl = this.$refs.fakeTooltip
      if (refEl) this.dispatchEventToRef({ refEl, eventName: 'mouseleave'})
    },
  },
}
</script>

<style>
.scale-page-enter-active,
.scale-page-leave-active {
  transition: all 0.3s ease;
}

.scale-page-enter-from,
.scale-page-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.main:has(.scale-page-enter-active),
.main:has(.scale-page-leave-active) {
  height: unset !important;
}

.app-root__loader {
  height: 100dvh;
  display: grid;
  place-items: center;
}
</style>
